import React, { useState, useEffect, useMemo, useContext } from "react";
import { Accordion } from "react-bootstrap";
import { useLocation, useNavigate, useParams, UNSAFE_NavigationContext } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import {
    createUpdateServiceRateCard,
    bulkCreateUpdateServiceRateCard,
    createCustomService,
    updateCustomService,
    removeCustomService
} from '../../store/actions/serviceRateCard.action';
import { getSelectList } from "../../config/utils";
import { fetchCompanyFilters } from "../../store/actions/rateCard";
import { getAllSellersList } from "../../store/actions";
import { getSellerWarehouseService } from "../../services/company.service";
import moment from "moment";
import { toast } from "react-toastify";
import _ from "lodash";
import ServiceRateCardComponent from "./Services";
import { getCompanySellerRateCardsService } from "../../store/actions/inventory.action";
import PropTypes from 'prop-types';
import { Tooltip as WmsTooltip } from "react-tooltip";
import AlertDialogModal from "../common/AlertDialog";
import CustomServiceTypeModal from "./CustomServiceTypeModal";
import { isSuperAdminRole } from "../../constants/roleConstants";

const CUSTOM_SERVICES_GROUP = 'Custom Services';

const toTime = (value) => (value ? moment(value).valueOf() : null);

const createNavigationGuard = (method, target, onBlocked) => (...args) => {
    onBlocked(() => method.apply(target, args));
};

// API returns UTC without a "Z" suffix, so parse explicitly as UTC.
const toLocalRateCard = (row) => ({
    ...row,
    ValidFrom: row.ValidFrom ? moment.utc(row.ValidFrom).toDate() : null,
    ValidTo: row.ValidTo ? moment.utc(row.ValidTo).toDate() : null,
});

const isRateCardChanged = (current, original) =>
    !!original && (
        Number(current.Amount) !== Number(original.Amount) ||
        Number(current.AdditionalCharge) !== Number(original.AdditionalCharge) ||
        toTime(current.ValidFrom) !== toTime(original.ValidFrom) ||
        toTime(current.ValidTo) !== toTime(original.ValidTo)
    );

/* istanbul ignore next */
const ServiceRateCard = (props) => {
    const location = useLocation();
    const { isEditable = false } = props;
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { filterCompanies } = useSelector((state) => state.RateCardReducer);
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [warehouseList, setWarehouseList] = useState([]);
    const [selectedWarehouse_Id, setSelectedWarehouse_Id] = useState(0);
    const rateCardDetails = location?.state?.rateCardDetails;
    const { showAccountingMenu } = useSelector((state) => state.accountingReducer);
    const loggedInUser = useSelector((state) => state?.auth?.user?.Value);

    const [error, setErrors] = useState({
        serviceDetails: [
            {
                serviceType_Id: "",
                transactionType_Id: "",
                packingType_Id: "",
                sizeType_Id: "",
                unit_Id: "",
                billingCycle_Id: "",
                amount: "",
                additionalCharge: "",
            }
        ]
    });

    const serviceRateCardObj = {
        isActive: true,
        id: 0,
        selectedCompany_Id: 0,
        selectedSeller_Id: 0,
        seller_Id: 0,
        serviceDetails: [
            {
                id: Number(id),
                details_Id: 0,
                serviceType_Id: 0,
                transactionType_Id: 0,
                packingType_Id: 0,
                sizeType_Id: 0,
                unit_Id: 0,
                billingCycle_Id: 0,
                amount: "",
                additionalCharge: "",
                isServiceActive: false,
            }
        ],
        selectedWarehouse_Id: 0
    }

    const [formData, setFormData] = useState({});
    const [originalServiceDetails, setOriginalServiceDetails] = useState([]);
    const [isBulkSaving, setIsBulkSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ show: false, type: '' });
    const [customServiceModal, setCustomServiceModal] = useState({ show: false, editItem: null });
    const [isCustomServiceSaving, setIsCustomServiceSaving] = useState(false);
    const [customServiceError, setCustomServiceError] = useState('');

    const handleBack = () => {
        navigate('/accounting/service-rate-card');
    };

    const fetchData = async (pendingRows = []) => {
        try {
            let data = {
                "pageIndex": 1,
                "pageSize": 200,
                "sortColumn": "",
                "sortOrder": "ASCENDING",
                "searchkey": "",
                "selectedCompany_Id": rateCardDetails?.CompanyId,
                "selectedSeller_Id": rateCardDetails?.SellerId,
                "selectedDateTime": moment.utc().format("YYYY-MM-DDTHH:mm:ss"),
                "selectedWarehouse_Id": selectedWarehouse_Id || rateCardDetails?.WarehouseId
            }
            const res = await getCompanySellerRateCardsService(data);
            const rateCards = res.data.Entity.RateCards?.map(toLocalRateCard);
            // Re-apply unsaved edits of rows that were not part of the save.
            const pendingById = _.keyBy(pendingRows, 'AccountingServiceMaster_Id');
            const mergedRateCards = rateCards?.map((row) => {
                const pending = pendingById[row.AccountingServiceMaster_Id];
                return pending
                    ? { ...row, Amount: pending.Amount, AdditionalCharge: pending.AdditionalCharge, ValidFrom: pending.ValidFrom, ValidTo: pending.ValidTo }
                    : row;
            });
            setFormData({ ...serviceRateCardObj, serviceDetails: mergedRateCards });
            setOriginalServiceDetails(_.cloneDeep(rateCards || []));
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (!rateCardDetails?.CompanyId) {
            navigate('/accounting/service-rate-card');
        }
    }, [rateCardDetails]);

    useEffect(() => {
        if (Number(id) > 0) {
            let data = {
                pageIndex: 1,
                pageSize: 10,
                sortColumn: "name",
                sortOrder: "ascending",
            };
            dispatch(fetchCompanyFilters(data));
            fetchData();
        }
    }, [id]);

    useEffect(() => {
        let companyList = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        setCompanyList(getSelectList(companyList, 'Id', 'name'));
    }, [filterCompanies]);

    const fetchSellers = async (activeSellerList) => {
        let data = {
            companyIds: [activeSellerList],
        };
        let res = await getAllSellersList(data);
        if (res?.data?.Status === 200) {
            let dataList = res?.data?.Entity?.listSellerMasterOrdersEntity.map(
                (item) => {
                    return {
                        ...item,
                        label: item.sellerAndCompany,
                        value: item.id,
                    };
                }
            );

            setSellerList(dataList);
        }
    };

    const fetchWarehouses = async (sellerId) => {
        try {
            let data = {
                companyId: rateCardDetails?.CompanyId,
                sellerId: sellerId,
                tenantId: 0 // This will be overridden by the server based on JWT token
            };
            let res = await getSellerWarehouseService(data);
            if (res?.data?.Status === 200 && res?.data?.EntityList) {
                let dataList = res.data.EntityList.map((item) => {
                    return {
                        id: item.Warehouse_Id,
                        label: item.WarehouseName,
                        value: item.Warehouse_Id,
                        ...item
                    };
                });
                setWarehouseList(dataList);
                // Set default warehouse to first item if not already set
                if (!selectedWarehouse_Id && dataList.length > 0) {
                    setSelectedWarehouse_Id(dataList[0].id);
                }
            } else {
                console.warn('No warehouses found or API error:', res?.data);
                setWarehouseList([]);
            }
        } catch (error) {
            console.error('Error fetching warehouses:', error);
            setWarehouseList([]);
        }
    };

    useEffect(() => {
        if (rateCardDetails?.CompanyId) {
            let companyData = rateCardDetails?.CompanyId;
            fetchSellers(companyData);
        }
    }, [rateCardDetails?.CompanyId]);

    useEffect(() => {
        if (rateCardDetails?.SellerId) {
            let sellerData = rateCardDetails?.SellerId;
            fetchWarehouses(sellerData);
        }
    }, [rateCardDetails?.SellerId]);

    // Fetch data again when selectedWarehouse_Id is set for the first time
    useEffect(() => {
        if (selectedWarehouse_Id > 0) {
            fetchData();
        }
    }, [selectedWarehouse_Id]);

    const handleServiceDetailChange = (event, index) => {
        const { value, name } = event.target;
        let data = _.cloneDeep(formData);
        let errorData = _.cloneDeep(error);
        data.serviceDetails[index][name] = value;
        setFormData(data);
        errorData.serviceDetails[index][name] = '';
        setErrors(errorData);
    }

    const handleInputBlur = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value.trim() });
        setErrors({ ...error, [name]: '' });
    };

    const handleDateChange = (value, name, index) => {
        let data = _.cloneDeep(formData);
        data.serviceDetails[index][name] = value;
        setFormData(data);
    }

    const changedRateCards = useMemo(() => {
        const originalById = _.keyBy(originalServiceDetails, 'AccountingServiceMaster_Id');
        return (formData?.serviceDetails || []).filter(
            (row) => isRateCardChanged(row, originalById[row.AccountingServiceMaster_Id])
        );
    }, [formData?.serviceDetails, originalServiceDetails]);

    const buildRateCardPayload = (rateCard) => {
        let dataSave = {
            ...rateCard,
            selectedCompany_Id: rateCardDetails?.CompanyId,
            selectedSeller_Id: rateCardDetails?.SellerId,
            selectedWarehouse_Id: selectedWarehouse_Id || rateCardDetails?.WarehouseId,
            ValidFrom: moment(rateCard.ValidFrom).toISOString(),
            ValidTo: rateCard.ValidTo ? moment(rateCard.ValidTo).toISOString() : null,
        };
        delete dataSave.IsDefaulRate;
        return dataSave;
    };

    const closeConfirmDialog = () => setConfirmDialog({ show: false, type: '' });

    const getPendingRowsExcept = (savedRows) => {
        const savedIds = new Set(savedRows.map((r) => r.AccountingServiceMaster_Id));
        return changedRateCards.filter((r) => !savedIds.has(r.AccountingServiceMaster_Id));
    };

    const requestSave = (rows, groupName = '') => {
        if (!rows.length) {
            toast.info("No changes to save.");
            return;
        }
        const missingFrom = rows.filter((r) => !r.ValidFrom);
        if (missingFrom.length) {
            toast.error(`Valid From is required for: ${missingFrom.map((r) => r.ServiceType).join(', ')}`);
            return;
        }
        const invalidDates = rows.filter(
            (r) => r.ValidTo && moment(r.ValidFrom).isAfter(r.ValidTo)
        );
        if (invalidDates.length) {
            toast.error(`Valid From date cannot be after Valid To date for: ${invalidDates.map((r) => r.ServiceType).join(', ')}`);
            return;
        }
        setConfirmDialog({ show: true, type: 'save', rows, groupName });
    };

    const requestDiscard = (rows, groupName = '') => {
        if (!rows.length) {
            return;
        }
        setConfirmDialog({ show: true, type: 'discard', rows, groupName });
    };

    const handleSaveAllClick = () => requestSave(changedRateCards);

    const handleBulkSave = async () => {
        const rows = confirmDialog.rows || [];
        setIsBulkSaving(true);
        try {
            const payload = rows.map(buildRateCardPayload);
            const res = await Promise.resolve(dispatch(bulkCreateUpdateServiceRateCard(payload)));
            if (res?.data?.Status === 200) {
                toast.success(`${payload.length} rate(s) updated successfully`);
                await fetchData(getPendingRowsExcept(rows));
            } else {
                toast.error(res?.data?.Message?.AppStatusDescription || "Unable to save rate cards. No changes were saved.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Unable to save rate cards. No changes were saved.");
        } finally {
            setIsBulkSaving(false);
            closeConfirmDialog();
        }
    };

    const handleDiscard = () => {
        const discardIds = new Set((confirmDialog.rows || []).map((r) => r.AccountingServiceMaster_Id));
        const originalById = _.keyBy(originalServiceDetails, 'AccountingServiceMaster_Id');
        setFormData({
            ...formData,
            serviceDetails: formData.serviceDetails.map((row) =>
                discardIds.has(row.AccountingServiceMaster_Id)
                    ? _.cloneDeep(originalById[row.AccountingServiceMaster_Id])
                    : row
            ),
        });
        closeConfirmDialog();
    };

    // react-router 6.4 BrowserRouter has no navigator.block, so guard push/replace used by navigate() and <Link>.
    const { navigator } = useContext(UNSAFE_NavigationContext);
    useEffect(() => {
        if (!changedRateCards.length) {
            return undefined;
        }
        const { push, replace } = navigator;
        const showLeaveConfirm = (proceed) => {
            setConfirmDialog({ show: true, type: 'back', proceed });
        };
        navigator.push = createNavigationGuard(push, navigator, showLeaveConfirm);
        navigator.replace = createNavigationGuard(replace, navigator, showLeaveConfirm);

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            navigator.push = push;
            navigator.replace = replace;
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [navigator, changedRateCards.length]);

    const handleConfirmLeave = () => {
        const proceed = confirmDialog.proceed;
        closeConfirmDialog();
        proceed?.();
    };

    const handleConfirmWarehouseChange = () => {
        setSelectedWarehouse_Id(confirmDialog.warehouseId);
        closeConfirmDialog();
    };

    const getCustomServiceScope = () => ({
        selectedCompany_Id: rateCardDetails?.CompanyId,
        selectedSeller_Id: rateCardDetails?.SellerId,
        selectedWarehouse_Id: selectedWarehouse_Id || rateCardDetails?.WarehouseId,
    });

    const openCustomServiceModal = (editItem = null) => {
        setCustomServiceError('');
        setCustomServiceModal({ show: true, editItem });
    };

    const closeCustomServiceModal = () => {
        setCustomServiceError('');
        setCustomServiceModal({ show: false, editItem: null });
    };

    const handleCustomServiceSubmit = async (form) => {
        const editItem = customServiceModal.editItem;
        const payload = {
            ...getCustomServiceScope(),
            serviceName: form.serviceName,
            transactionType: form.transactionType,
            chargeType: form.chargeType,
        };
        if (editItem) {
            payload.accountingServiceMaster_Id = editItem.AccountingServiceMaster_Id;
        } else {
            payload.validFrom = moment(form.validFrom).toISOString();
            payload.validTo = form.validTo ? moment(form.validTo).toISOString() : null;
            payload.amount = Number(form.amount);
            payload.additionalCharge = Number(form.additionalCharge || 0);
        }

        setIsCustomServiceSaving(true);
        setCustomServiceError('');
        try {
            const action = editItem ? updateCustomService(payload) : createCustomService(payload);
            const res = await Promise.resolve(dispatch(action));
            if (res?.data?.Status === 200) {
                toast.success(res?.data?.Message?.AppStatusDescription || 'Custom service type saved successfully.');
                setCustomServiceModal({ show: false, editItem: null });
                await fetchData(changedRateCards);
            } else {
                setCustomServiceError(res?.data?.Message?.AppStatusDescription || 'Unable to save custom service type.');
            }
        } catch (err) {
            console.error(err);
            setCustomServiceError('Unable to save custom service type.');
        } finally {
            setIsCustomServiceSaving(false);
        }
    };

    const requestRemoveCustomService = (row) => {
        setConfirmDialog({ show: true, type: 'removeCustom', row });
    };

    const handleRemoveCustomService = async () => {
        const row = confirmDialog.row;
        setIsCustomServiceSaving(true);
        try {
            const res = await Promise.resolve(dispatch(removeCustomService({
                ...getCustomServiceScope(),
                accountingServiceMaster_Id: row.AccountingServiceMaster_Id,
            })));
            if (res?.data?.Status === 200) {
                toast.success(res?.data?.Message?.AppStatusDescription || 'Custom service type removed successfully.');
                await fetchData(getPendingRowsExcept([row]));
            } else {
                toast.error(res?.data?.Message?.AppStatusDescription || 'Unable to remove custom service type.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Unable to remove custom service type.');
        } finally {
            setIsCustomServiceSaving(false);
            closeConfirmDialog();
        }
    };

    const confirmRowCount = confirmDialog.rows?.length || 0;
    // Rendered as React nodes so service names are escaped (string messages are injected as HTML).
    const groupSuffix = confirmDialog.groupName ? <> in <b>{confirmDialog.groupName}</b></> : null;
    const confirmConfig = {
        save: { heading: confirmDialog.groupName ? 'SAVE CHANGES' : 'SAVE ALL', message: <>Save {confirmRowCount} change(s){groupSuffix}?</>, onYes: handleBulkSave },
        discard: { heading: 'DISCARD CHANGES', message: <>Discard {confirmRowCount} unsaved change(s){groupSuffix}?</>, onYes: handleDiscard },
        back: { heading: 'DISCARD CHANGES', message: `Discard ${changedRateCards.length} unsaved change(s) and leave this page?`, onYes: handleConfirmLeave },
        warehouse: { heading: 'UNSAVED CHANGES', message: `You have ${changedRateCards.length} unsaved change(s). Changing warehouse will discard them. Continue?`, onYes: handleConfirmWarehouseChange },
        removeCustom: {
            heading: 'REMOVE CUSTOM SERVICE TYPE',
            message: <>Remove <b>{confirmDialog.row?.ServiceType}</b>? It will stop from now; history is kept.</>,
            onYes: handleRemoveCustomService,
        },
    }[confirmDialog.type];

    const changedCountLabel = changedRateCards.length ? ' (' + changedRateCards.length + ')' : '';
    const saveAllLabel = isBulkSaving ? 'Saving...' : 'Save All' + changedCountLabel;

    const handleCreateUpdateServiceRateCard = async (rateCard, actionType) => {
        let dataSave = buildRateCardPayload(rateCard);
        if (rateCard.ValidFrom && rateCard.ValidTo) {
            if (moment(rateCard.ValidFrom).isAfter(rateCard.ValidTo)) {
                toast.error("Valid From date cannot be after Valid To date.");
                return;
            }
        }
        let res = await Promise.resolve(dispatch(createUpdateServiceRateCard(dataSave)));
        if (res?.data?.Status === 200) {
            toast.success(`Updated successfully`);
            fetchData(getPendingRowsExcept([rateCard]));
        } else {
            toast.error(res?.data?.Message?.AppStatusDescription);
        }
    };

    const handleServiceDetails = (value, name, index, isNumber = false) => {
        let data = _.cloneDeep(formData);
        data.serviceDetails[index][name] = isNumber ? Number(value) : value;
        setFormData(data);
    };

    const handleWarehouseChange = (e) => {
        const warehouseId = Number.parseInt(e.target.value);
        if (changedRateCards.length) {
            setConfirmDialog({ show: true, type: 'warehouse', warehouseId });
            return;
        }
        setSelectedWarehouse_Id(warehouseId);
    };

    const getUserContext = () => {
        const userDetails = JSON.parse(localStorage.getItem('currentUser') || '{}');
        return userDetails?.Value || {};
    };

    const currentUser = loggedInUser || getUserContext();
    const canManageCustomServices = Boolean(currentUser?.isSuperAdmin ?? currentUser?.IsSuperAdmin) || isSuperAdminRole(currentUser);

    const buildCustomTemplateFilterPayload = (rateCardItem) => {
        const userValue = getUserContext();
        const serviceTypeMasterId = Number(rateCardItem?.ServiceTypeMaster_Id || 0);
        const warehouseId = Number(selectedWarehouse_Id || rateCardDetails?.WarehouseId || 0);

        return {
            pageIndex: 1,
            pageSize: 20,
            sortColumn: 'createdDate',
            sortOrder: 'ASCENDING',
            searchkey: '',
            companySellerId: userValue?.companySellerId || 0,
            role: userValue?.roleId || 0,
            isSuperAdmin: !!userValue?.isSuperAdmin,
            tenantId: userValue?.tenantId || 0,
            isBillingCycle: false,
            userEmail: userValue?.userEmailId || userValue?.email || '',
            warehouseList: warehouseId > 0 ? [warehouseId] : [],
            sellerIdList: [Number(rateCardDetails?.SellerId || 0)].filter((id) => id > 0),
            companyIdList: [Number(rateCardDetails?.CompanyId || 0)].filter((id) => id > 0),
            serviceTypes: serviceTypeMasterId > 0 ? [serviceTypeMasterId] : [],
            subServiceTypes: [],
            skuList: [],
        };
    };

    const handleCustomTemplateIconClick = (event, rateCardItem) => {
        event.stopPropagation();
        if (!rateCardItem) {
            return;
        }

        const payload = buildCustomTemplateFilterPayload(rateCardItem);
        navigate('/accounting/pricing-templates', {
            state: {
                preAppliedFilters: {
                    warehouseList: payload?.warehouseList || [],
                    companyIdList: payload?.companyIdList || [],
                    sellerIdList: payload?.sellerIdList || [],
                    serviceTypes: payload?.serviceTypes || [],
                    subServiceTypes: payload?.subServiceTypes || [],
                    skuList: payload?.skuList || [],
                },
                returnPath: location?.pathname,
                returnState: location?.state,
            },
        });
    };

    return (
        <div className={` ${showAccountingMenu ? 'col-10' : 'col-12'}`}>
            <div>
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>
                        {id > 0 && "Edit Service Rate Card"}
                    </span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {canManageCustomServices && (
                                <button
                                    className="btn cus-seconday-bg-btn"
                                    onClick={() => openCustomServiceModal()}
                                    disabled={isBulkSaving || isCustomServiceSaving}
                                >
                                    <i className="fa fa-plus" aria-hidden="true"></i> Add New Custom Service Type
                                </button>
                            )}
                            <button
                                className="btn cus-seconday-bg-btn marginLeft5"
                                onClick={handleSaveAllClick}
                                disabled={!changedRateCards.length || isBulkSaving}
                            >
                                {saveAllLabel}
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn marginLeft5"
                                onClick={() => requestDiscard(changedRateCards)}
                                disabled={!changedRateCards.length || isBulkSaving}
                            >
                                Discard All
                            </button>
                            <button
                                className="btn cus-primary-transparent-btn marginLeft5"
                                onClick={handleBack}
                                disabled={isBulkSaving}
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>

                <div className="cus-page-content-sec">
                    <div className="incomingAccordion accordion">
                        <div className="row fontFix">
                            <div className="col-lg-12">
                                <Accordion
                                    defaultActiveKey={["0", "1"]}
                                    alwaysOpen={true}
                                >
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            General{" "}
                                            <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="row addressForm">
                                                <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="selectedCompany_Id">Company <span className="error">*</span></label>
                                                    <select
                                                        className="form-control selectedCompany_Id"
                                                        value={rateCardDetails?.CompanyId}
                                                        id="selectedCompany_Id"
                                                        name="selectedCompany_Id"
                                                        disabled
                                                    >
                                                        <option value="">Select company..</option>
                                                        {companyList.map((company) => (
                                                            <option key={company.Id} value={company.Id}>
                                                                {company.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className='require-field'>{error.selectedCompany_Id}</div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="selectedSeller_Id">Seller <span className="error">*</span></label>
                                                    <select
                                                        className="form-control selectedSeller_Id"
                                                        value={rateCardDetails?.SellerId}
                                                        id="selectedSeller_Id"
                                                        name="selectedSeller_Id"
                                                        disabled
                                                    >
                                                        <option value="">Select seller..</option>
                                                        {sellerList.map((seller) => (
                                                            <option key={seller.id} value={seller.id}>
                                                                {seller.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className='require-field'>{error.selectedSeller_Id}</div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="selectedWarehouse_Id">Warehouse <span className="error">*</span></label>
                                                    <select
                                                        className="form-control selectedWarehouse_Id"
                                                        value={selectedWarehouse_Id || rateCardDetails?.WarehouseId}
                                                        id="selectedWarehouse_Id"
                                                        name="selectedWarehouse_Id"
                                                        onChange={handleWarehouseChange}
                                                        disabled={isBulkSaving}
                                                    >
                                                        <option value="">Select warehouse..</option>
                                                        {warehouseList.map((warehouse) => (
                                                            <option key={warehouse.id} value={warehouse.id}>
                                                                {warehouse.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className='require-field'>{error.selectedWarehouse_Id}</div>
                                                </div>
                                                <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                                    <label htmlFor="selectedSellerCode">Seller Code</label>
                                                    <input
                                                        type='text'
                                                        className='form-control'
                                                        id="selectedSellerCode"
                                                        value={rateCardDetails?.SellerCode}
                                                        disabled
                                                    />
                                                </div>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>

                                    <Accordion.Item eventKey="1">
                                        <Accordion.Header>
                                            Services{" "}
                                            <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="nested-services-container">
                                                {(() => {
                                                    // Group services by ServiceType with special handling for rent services
                                                    const serviceGroups = {};
                                                    formData?.serviceDetails?.forEach((service, index) => {
                                                        // Get the service type
                                                        const originalServiceType = service.ServiceType || 'default';
                                                        
                                                        // Group "Base Rent" and "Variable Rent" under "Rent"
                                                        // Group all "Carrier Connect *" variants under "Carrier Connect"
                                                        let serviceName;
                                                        if (service.IsCustomService) {
                                                            serviceName = CUSTOM_SERVICES_GROUP;
                                                        } else if (originalServiceType.includes('Rent')) {
                                                            serviceName = 'Rent';
                                                        } else if (originalServiceType.startsWith('Carrier Connect')) {
                                                            serviceName = 'Carrier Connect';
                                                        } else {
                                                            serviceName = originalServiceType;
                                                        }
                                                        
                                                        if (!serviceGroups[serviceName]) {
                                                            serviceGroups[serviceName] = [];
                                                        }
                                                        serviceGroups[serviceName].push({...service, originalIndex: index});
                                                    });

                                                    // Super Admin always sees the section so the first custom service can be added.
                                                    if (canManageCustomServices && !serviceGroups[CUSTOM_SERVICES_GROUP]) {
                                                        serviceGroups[CUSTOM_SERVICES_GROUP] = [];
                                                    }

                                                    // Keep the Custom Services section last.
                                                    const groupNames = Object.keys(serviceGroups).sort((a, b) =>
                                                        Number(a === CUSTOM_SERVICES_GROUP) - Number(b === CUSTOM_SERVICES_GROUP));

                                                    return groupNames.map((serviceName, groupIndex) => {
                                                        const services = serviceGroups[serviceName];
                                                        const displayName = serviceName === 'default' ? 'Undefined Service' : serviceName;
                                                        const hasCustomTemplate = services.some((service) => Number(service?.IsCustomTemplateExists) === 1);
                                                        const selectedRateCard = services.find((service) => Number(service?.IsCustomTemplateExists) === 1) || services[0];
                                                        const groupIds = new Set(services.map((service) => service.AccountingServiceMaster_Id));
                                                        const groupChangedRows = changedRateCards.filter((row) => groupIds.has(row.AccountingServiceMaster_Id));
                                                        const isGroupActionDisabled = !groupChangedRows.length || isBulkSaving;
                                                        const showAddCustomService = canManageCustomServices && serviceName === CUSTOM_SERVICES_GROUP;

                                                        return (
                                                            <div key={`service-group-${serviceName}-${groupIndex}`} className="nested-service-item">
                                                                <div className="nested-accordion-wrapper">
                                                                    <Accordion defaultActiveKey="0" alwaysOpen={false} className="child-service-accordion">
                                                                        <Accordion.Item eventKey="0" className="child-accordion-item">
                                                                            <div className="child-header-row">
                                                                                <div className={`group-actions${hasCustomTemplate ? ' with-template' : ''}`}>
                                                                                    {showAddCustomService && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn group-action-btn group-action-add"
                                                                                            title="Add new custom service type"
                                                                                            disabled={isBulkSaving || isCustomServiceSaving}
                                                                                            onClick={() => openCustomServiceModal()}
                                                                                        >
                                                                                            <i className="fa fa-plus" aria-hidden="true"></i>
                                                                                            <span>Add New Custom Service Type</span>
                                                                                        </button>
                                                                                    )}
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn group-action-btn group-action-save"
                                                                                        title="Save changes in this service"
                                                                                        disabled={isGroupActionDisabled}
                                                                                        onClick={() => requestSave(groupChangedRows, displayName)}
                                                                                    >
                                                                                        <i className="fa fa-check" aria-hidden="true"></i>
                                                                                        <span>Save</span>
                                                                                        {groupChangedRows.length > 0 && <span className="group-action-count">{groupChangedRows.length}</span>}
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn group-action-btn group-action-discard"
                                                                                        title="Discard changes in this service"
                                                                                        disabled={isGroupActionDisabled}
                                                                                        onClick={() => requestDiscard(groupChangedRows, displayName)}
                                                                                    >
                                                                                        <i className="fa fa-undo" aria-hidden="true"></i>
                                                                                        <span>Discard</span>
                                                                                    </button>
                                                                                </div>
                                                                                {hasCustomTemplate && (
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn iconBtns small custom-template-icon-btn"
                                                                                        onClick={(event) => handleCustomTemplateIconClick(event, selectedRateCard)}
                                                                                        title="Custom pricing template exists"
                                                                                        data-tooltip-id="custom-template-exists-tooltip"
                                                                                        data-tooltip-content="Custom pricing template exists. Click to fetch templates."
                                                                                    >
                                                                                        <i className="fa fa-tags" aria-hidden="true"></i>
                                                                                    </button>
                                                                                )}
                                                                                <Accordion.Header className="child-service-header">
                                                                                    <span className="service-type-title d-inline-flex align-items-center">
                                                                                        <span>{displayName}</span>
                                                                                    </span>
                                                                                    <i className="fa fa-chevron-down" aria-hidden="true"></i>
                                                                                </Accordion.Header>
                                                                            </div>
                                                                            <Accordion.Body className="child-service-body">
                                                                                <ServiceRateCardComponent
                                                                                    handleServiceDetails={(value, name, index, isNumber) => {
                                                                                        const originalIndex = services[index]?.originalIndex;
                                                                                        if (originalIndex !== undefined) {
                                                                                            handleServiceDetails(value, name, originalIndex, isNumber);
                                                                                        }
                                                                                    }}
                                                                                    handleInputBlur={handleInputBlur}
                                                                                    isEditable={isEditable}
                                                                                    formData={{
                                                                                        ...formData,
                                                                                        serviceDetails: services.map(s => {
                                                                                            const {originalIndex, ...service} = s;
                                                                                            return service;
                                                                                        })
                                                                                    }}
                                                                                    error={{
                                                                                        ...error,
                                                                                        serviceDetails: services.map(s => 
                                                                                            error.serviceDetails[s.originalIndex] || {
                                                                                                serviceType_Id: "",
                                                                                                transactionType_Id: "",
                                                                                                packingType_Id: "",
                                                                                                sizeType_Id: "",
                                                                                                unit_Id: "",
                                                                                                billingCycle_Id: "",
                                                                                                amount: "",
                                                                                                additionalCharge: "",
                                                                                            }
                                                                                        )
                                                                                    }}
                                                                                    handleFormChange={(event, index) => {
                                                                                        const originalIndex = services[index]?.originalIndex;
                                                                                        if (originalIndex !== undefined) {
                                                                                            handleServiceDetailChange(event, originalIndex);
                                                                                        }
                                                                                    }}
                                                                                    handleDateChange={(value, name, index) => {
                                                                                        const originalIndex = services[index]?.originalIndex;
                                                                                        if (originalIndex !== undefined) {
                                                                                            handleDateChange(value, name, originalIndex);
                                                                                        }
                                                                                    }}
                                                                                    handleSaveService={handleCreateUpdateServiceRateCard}
                                                                                    isBulkSaving={isBulkSaving || isCustomServiceSaving}
                                                                                    canManageCustomServices={canManageCustomServices}
                                                                                    onEditCustomService={openCustomServiceModal}
                                                                                    onRemoveCustomService={requestRemoveCustomService}
                                                                                    rateCardDetails={rateCardDetails}
                                                                                />
                                                                            </Accordion.Body>
                                                                        </Accordion.Item>
                                                                    </Accordion>
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })()}
                                                <WmsTooltip id="custom-template-exists-tooltip" />
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AlertDialogModal
                modalShow={confirmDialog.show}
                headingText={confirmConfig?.heading}
                messageText={confirmConfig?.message}
                handleYesDeleteAlert={confirmConfig?.onYes}
                handleCancelDeleteAlert={closeConfirmDialog}
                loader={isBulkSaving || isCustomServiceSaving}
            />
            <CustomServiceTypeModal
                show={customServiceModal.show}
                editItem={customServiceModal.editItem}
                isSaving={isCustomServiceSaving}
                apiError={customServiceError}
                onClose={closeCustomServiceModal}
                onSubmit={handleCustomServiceSubmit}
            />
        </div>
    );
}

ServiceRateCard.propTypes = {
    isEditable: PropTypes.bool,
};

export default ServiceRateCard;